import { useState, useEffect } from 'react';
import { Package, OrganizationSubscription } from '../types/package';
import { packageService } from '../services/packageService';
import { useAuth } from '../contexts/AuthContext';

export const usePackages = () => {
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedPackages = await packageService.getAllPackages();
      setPackages(fetchedPackages);
    } catch (err) {
      console.error('Error fetching packages:', err);
      setError('Hiba történt a csomagok betöltése során');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  const createPackage = async (packageData: Omit<Package, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const id = await packageService.createPackage(packageData);
      await fetchPackages(); // Refresh the list
      return id;
    } catch (err) {
      console.error('Error creating package:', err);
      throw new Error('Hiba történt a csomag létrehozása során');
    }
  };

  const updatePackage = async (id: string, packageData: Partial<Package>) => {
    try {
      await packageService.updatePackage(id, packageData);
      await fetchPackages(); // Refresh the list
    } catch (err) {
      console.error('Error updating package:', err);
      throw new Error('Hiba történt a csomag frissítése során');
    }
  };

  const deletePackage = async (id: string) => {
    try {
      await packageService.deletePackage(id);
      await fetchPackages(); // Refresh the list
    } catch (err) {
      console.error('Error deleting package:', err);
      throw new Error('Hiba történt a csomag törlése során');
    }
  };

  return {
    packages,
    loading,
    error,
    fetchPackages,
    createPackage,
    updatePackage,
    deletePackage
  };
};

export const useSubscriptions = () => {
  const [subscriptions, setSubscriptions] = useState<OrganizationSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedSubscriptions = await packageService.getAllSubscriptions();
      setSubscriptions(fetchedSubscriptions);
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
      setError('Hiba történt az előfizetések betöltése során');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const createSubscription = async (subscriptionData: Omit<OrganizationSubscription, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const id = await packageService.createSubscription(subscriptionData);
      await fetchSubscriptions(); // Refresh the list
      return id;
    } catch (err) {
      console.error('Error creating subscription:', err);
      throw new Error('Hiba történt az előfizetés létrehozása során');
    }
  };

  const updateSubscription = async (id: string, subscriptionData: Partial<OrganizationSubscription>) => {
    try {
      await packageService.updateSubscription(id, subscriptionData);
      await fetchSubscriptions(); // Refresh the list
    } catch (err) {
      console.error('Error updating subscription:', err);
      throw new Error('Hiba történt az előfizetés frissítése során');
    }
  };

  const deleteSubscription = async (id: string) => {
    try {
      await packageService.deleteSubscription(id);
      await fetchSubscriptions(); // Refresh the list
    } catch (err) {
      console.error('Error deleting subscription:', err);
      throw new Error('Hiba történt az előfizetés törlése során');
    }
  };

  return {
    subscriptions,
    loading,
    error,
    fetchSubscriptions,
    createSubscription,
    updateSubscription,
    deleteSubscription
  };
};

export const useOrganizationSubscription = (organizationId?: string) => {
  const [subscription, setSubscription] = useState<OrganizationSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!organizationId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const subscriptions = await packageService.getSubscriptionsByOrganization(organizationId);
        // Aktív vagy próbaidős előfizetést keresünk
        const activeSubscription = subscriptions.find(sub => 
          sub.status === 'active' || sub.status === 'trial'
        );
        setSubscription(activeSubscription || null);
      } catch (err) {
        console.error('Error fetching organization subscription:', err);
        setError('Hiba történt az előfizetés betöltése során');
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [organizationId]);

  return {
    subscription,
    loading,
    error
  };
};

export const useModuleAccess = () => {
  const { user } = useAuth();

  const checkModuleAccess = async (moduleId: string): Promise<boolean> => {
    if (!user?.organizationId) {
      return false;
    }

    try {
      return await packageService.checkModuleAccess(user.organizationId, moduleId);
    } catch (error) {
      console.error('Error checking module access:', error);
      return false;
    }
  };

  const getAvailableModules = async () => {
    if (!user?.organizationId) {
      return [];
    }

    try {
      const subscriptions = await packageService.getSubscriptionsByOrganization(user.organizationId);
      const activeSubscription = subscriptions.find(sub => 
        sub.status === 'active' || sub.status === 'trial'
      );

      if (!activeSubscription) {
        return [];
      }

      return await packageService.getModulesByPackage(activeSubscription.packageId);
    } catch (error) {
      console.error('Error getting available modules:', error);
      return [];
    }
  };

  return {
    checkModuleAccess,
    getAvailableModules
  };
};

export const usePackageStats = () => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const packageStats = await packageService.getPackageStats();
      setStats(packageStats);
    } catch (err) {
      console.error('Error fetching package stats:', err);
      setError('Hiba történt a statisztikák betöltése során');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  return {
    stats,
    loading,
    error,
    fetchStats
  };
};
